import EventManager from '../eventManager/EventManager'
import { addClass, getContentWidth, writeCss } from '../helpers/DOM'
import Waypoint from '../lazyable/waypoints/WaypointAdapter'
import WebModel from '../models/WebModel'
import { Slot } from '../slots/Slot'

const MEDIAVINE_BRANDING_HEIGHT = 15
const defaultStickyWrapperTop = 10
const PIXEL_BUFFER = 120

export class StickySlot {
  private recalculate = {}
  private slotWaypoints = {}
  private slotStopWaypoints = {}

  constructor(private model: WebModel) {
    EventManager.on(EventManager.events.slotWrapperRenderEnded, (slot) => {
      this.handleSticky(slot)
    })
  }

  // NOTE: Call this in the constructor, after this.adUnitId has been set.
  handleSticky(slot: Slot): Slot {
    if (slot.sticky) {
      this.wrapSticky(slot)
      this.writeStickyCss(slot)

      EventManager.on(
        EventManager.events.slotRenderEnded,
        (event, eventSlot) => {
          if (
            eventSlot === slot // &&
          ) {
            this.stickySlotRenderEnded(event, eventSlot)
          }
        }
      )
    }

    return slot
  }

  private writeStickyCss(slot: Slot): string {
    const slotStickyOffset = slot.stickyOffset
    const stickyStyles = `
      #${slot.adUnitId}_sticky_wrapper.stuck {
        position : fixed;
        top : ${
          slotStickyOffset ? slotStickyOffset : defaultStickyWrapperTop
        }px;
      }
    `
    writeCss(stickyStyles)

    return stickyStyles
  }

  private wrapSticky(slot: Slot): void {
    const placeholder = document.createElement('div')
    placeholder.id = `${slot.adUnitId}_placeholder`
    addClass(placeholder, `${slot.adUnitId}_placeholder`)

    const stickyWrapper = document.createElement('div')
    stickyWrapper.id = `${slot.adUnitId}_sticky_wrapper`

    const slotWrapper = slot.wrapper
    wrapElement(slotWrapper, stickyWrapper)
    wrapElement(stickyWrapper, placeholder)
  }

  private stickySlotRenderEnded(event, slot: Slot): void | boolean {
    const adUnitId = slot.adUnitId
    const element = document.getElementById(
      `${adUnitId}_sticky_wrapper`
    ) as HTMLElement
    const placeholder = document.getElementById(
      `${adUnitId}_placeholder`
    ) as HTMLElement

    if (!slot.selector || !slot.sticky) {
      return false
    }
    if (event.isEmpty) {
      return false
    }
    if (placeholder === null) {
      return false
    }

    this.destroyOldWaypoints(slot)

    // Placeholder should set height to where ad was. Add some height for mediavine branding
    let adHeight = Number(event.size[1])
    if (adHeight === 1 && slot.highestPrebid) {
      // Prebid won, use highestPrebid.height
      adHeight = slot.highestPrebid.height || 300
    }
    adHeight += MEDIAVINE_BRANDING_HEIGHT
    placeholder.style.height = `${adHeight}px`

    // Wrapper width should equal sidebar width
    const placeholderParentNode = placeholder.parentNode as HTMLElement
    const minimumSidebarWidth = 300
    element.style.width = `${Math.max(
      getContentWidth(placeholderParentNode),
      minimumSidebarWidth
    )}px`

    let stopElement: HTMLElement | null = null
    if (slot.stickyStopSelector) {
      stopElement = document.querySelector(slot.stickyStopSelector)
    } else if (this.model.leaderboard_btf_selector) {
      // Default to leaderboard_btf_waypoint
      stopElement = document.getElementById('leaderboard_btf_waypoint')
    }

    const stickyOffset = slot.stickyOffset || 10

    // Waypoint to sticky
    this.slotWaypoints[slot.adUnitId] = new Waypoint({
      element: placeholder,
      handler(direction): void {
        if (direction === 'down') {
          if (stopElement !== null) {
            const stickyScrollHeight =
              stopElement.offsetTop - placeholder.offsetTop
            const minimumStickyScrollHeight =
              placeholder.offsetHeight * 2 + PIXEL_BUFFER

            // Make sticky if enough room. Extra padding in case we add margin.
            // This prevents the ad from becoming sticky if it won't have very much
            // room to scroll before if becomes unsticky again.
            if (stickyScrollHeight > minimumStickyScrollHeight) {
              element.className = 'stuck'
            } else {
              element.className = ''
            }
          } else {
            // Stop stop element. Make sticky.
            element.className = 'stuck'
          }
        } else {
          element.className = ''
        }
      },
      offset: stickyOffset
    })

    if (stopElement !== null) {
      // Waypoint to unstick at footer
      const offsetBuffer = 5
      this.slotStopWaypoints[slot.adUnitId] = new Waypoint({
        element: stopElement,
        handler(direction): void {
          if (direction === 'down') {
            // Unstick on way down
            element.className = ''
          } else {
            const stickyScrollHeight =
              this.element.offsetTop - placeholder.offsetTop
            const minimumStickyScrollHeight =
              placeholder.offsetHeight * 2 + PIXEL_BUFFER

            // Return to sticky if enough room. Extra padding in case we add margin.
            if (stickyScrollHeight > minimumStickyScrollHeight) {
              element.className = 'stuck'
            } else {
              element.className = ''
            }
          }
        },
        offset: adHeight + offsetBuffer + stickyOffset
      })
    }

    const setIntervalTime = 1000
    const clearIntervalTimeout = 120000

    // Auto recalculate waypoints every second for 2 minutes. Beyond that they likely left page.
    this.recalculate[slot.id] = setInterval(() => {
      Waypoint.Context.refreshAll()
    }, setIntervalTime)

    setTimeout(() => {
      clearInterval(this.recalculate[slot.id])
    }, clearIntervalTimeout)
  }

  private destroyOldWaypoints(slot): void {
    if (this.slotWaypoints[slot]) {
      this.slotWaypoints[slot].destroy()
    }
    if (this.slotStopWaypoints[slot]) {
      this.slotStopWaypoints[slot].destroy()
    }
  }
}

function wrapElement(el, wrapper): void {
  el.parentNode.insertBefore(wrapper, el)
  wrapper.appendChild(el)
}
